/**
 * availabilityStore — pure date/coalescing logic plus a debounced, self-persisting
 * store for household-level availability rows.
 *
 * Owned by spz-06-availability-grid. Consumed by src/components/AvailabilityGrid.tsx
 * (and, transitively, by the intake page in spz-05).
 *
 * The load-bearing invariant: **undeclared time is `'possible'`**. The window engine
 * (spz-07) treats `'possible'` as attendable, so `'possible'` is never persisted.
 * Cycling a day back to `'possible'` deletes it from the saved rows.
 */

import type { SupabaseClient } from '@supabase/supabase-js'

// ---------------------------------------------------------------------------
// Contract: db-schema-types — `src/lib/types.ts`, owned by spz-01-db-schema-rls.
// This module declares no row shapes of its own. The two names are re-exported so
// consumers that already import them from here keep working, but `types.ts` is the
// single definition; fix it there, never shadow it locally.
//
// Note on `Availability.person_id`: it is ALWAYS null for rows this module writes.
// Person-level rows are read-only here and are skipped by expandRows().
// ---------------------------------------------------------------------------

import type { Availability, AvailabilityLevel } from './types'

export type { Availability, AvailabilityLevel }

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** ISO date string, 'YYYY-MM-DD'. */
export type ISODate = string

/** The level assumed for any day nobody has declared. Never persisted. */
export const DEFAULT_LEVEL: AvailabilityLevel = 'possible'

/**
 * Day the grid's week columns begin on. 0 = Sunday … 6 = Saturday.
 *
 * OPEN QUESTION (see issue #7): set to Saturday, the standard vacation-rental
 * turnover day, so a week column maps 1:1 onto a bookable rental week. The family
 * has historically booked Friday–Monday; if Friday is the right boundary this is a
 * one-line change to `5` and every column shifts with it. Nothing else in this
 * module hardcodes a week boundary.
 */
export const WEEK_START_DAY = 6

/** Days per week column. */
export const DAYS_PER_WEEK = 7

const MS_PER_DAY = 86_400_000

// ---------------------------------------------------------------------------
// Date math — string/ordinal only, always UTC. Never `new Date('2027-07-10')`
// plus local getters; that shifts a day for anyone west of UTC.
// ---------------------------------------------------------------------------

/** Days since the Unix epoch for an ISO date. */
export function toOrdinal(iso: ISODate): number {
  const y = Number(iso.slice(0, 4))
  const m = Number(iso.slice(5, 7))
  const d = Number(iso.slice(8, 10))
  return Math.floor(Date.UTC(y, m - 1, d) / MS_PER_DAY)
}

/** Inverse of {@link toOrdinal}. */
export function fromOrdinal(n: number): ISODate {
  return new Date(n * MS_PER_DAY).toISOString().slice(0, 10)
}

/** `iso` shifted by `n` days (n may be negative). */
export function addDays(iso: ISODate, n: number): ISODate {
  return fromOrdinal(toOrdinal(iso) + n)
}

/** 0 = Sunday … 6 = Saturday. 1970-01-01 was a Thursday (4). */
export function dayOfWeek(iso: ISODate): number {
  return (((toOrdinal(iso) + 4) % 7) + 7) % 7
}

/** The {@link WEEK_START_DAY} on or before `iso`. */
export function startOfWeek(iso: ISODate, weekStartDay: number = WEEK_START_DAY): ISODate {
  const back = (((dayOfWeek(iso) - weekStartDay) % 7) + 7) % 7
  return addDays(iso, -back)
}

/** Inclusive list of ISO dates from `start` to `end`. Empty if end < start. */
export function eachDay(start: ISODate, end: ISODate): ISODate[] {
  const out: ISODate[] = []
  for (let n = toOrdinal(start), last = toOrdinal(end); n <= last; n++) out.push(fromOrdinal(n))
  return out
}

// ---------------------------------------------------------------------------
// Pure expansion / coalescing
// ---------------------------------------------------------------------------

/**
 * Dense per-day view of the horizon. Every day in `horizonStart..horizonEnd` is
 * present; days no row covers are {@link DEFAULT_LEVEL}.
 *
 * Person-level rows (`person_id !== null`) are ignored entirely — this grid is a
 * household-level UI. Later rows win on overlap.
 */
export function expandRows(
  rows: Availability[],
  horizonStart: ISODate,
  horizonEnd: ISODate,
): Map<ISODate, AvailabilityLevel> {
  const days = new Map<ISODate, AvailabilityLevel>()
  for (const d of eachDay(horizonStart, horizonEnd)) days.set(d, DEFAULT_LEVEL)

  const lo = toOrdinal(horizonStart)
  const hi = toOrdinal(horizonEnd)

  for (const row of rows) {
    if (row.person_id !== null) continue
    const from = Math.max(lo, toOrdinal(row.start_date))
    const to = Math.min(hi, toOrdinal(row.end_date))
    for (let n = from; n <= to; n++) days.set(fromOrdinal(n), row.level)
  }
  return days
}

/** A row as this module persists it: no id, and trip/household supplied by the store. */
export type AvailabilityDraft = Omit<Availability, 'id' | 'trip_id' | 'household_id'>

/**
 * Run-length encode a dense day map into the minimum set of persisted rows.
 *
 * `'possible'` runs are dropped (that is the default; persisting it would be a lie
 * the window engine has to undo). Runs break on a level change *or* a gap in dates,
 * so `expandRows(coalesce(m))` round-trips to `m` exactly.
 */
export function coalesce(days: Map<ISODate, AvailabilityLevel>): AvailabilityDraft[] {
  const sorted = [...days.keys()].sort()
  const out: AvailabilityDraft[] = []

  let runStart: ISODate | null = null
  let runEnd: ISODate | null = null
  let runLevel: AvailabilityLevel | null = null

  const flush = () => {
    if (runStart !== null && runEnd !== null && runLevel !== null && runLevel !== DEFAULT_LEVEL) {
      out.push({ person_id: null, start_date: runStart, end_date: runEnd, level: runLevel, note: null })
    }
    runStart = runEnd = runLevel = null
  }

  for (const day of sorted) {
    const level = days.get(day) ?? DEFAULT_LEVEL
    const contiguous = runEnd !== null && toOrdinal(day) === toOrdinal(runEnd) + 1
    if (runLevel === level && contiguous) {
      runEnd = day
      continue
    }
    flush()
    runStart = day
    runEnd = day
    runLevel = level
  }
  flush()

  return out
}

/** preferred → possible → blackout → preferred. */
export function nextLevel(l: AvailabilityLevel): AvailabilityLevel {
  return l === 'preferred' ? 'possible' : l === 'possible' ? 'blackout' : 'preferred'
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

/**
 * Minimal persistence surface so tests can mock it. The real Supabase anon client
 * satisfies it via {@link supabaseAvailabilityClient}.
 */
export interface AvailabilityClient {
  /** Household-level rows only (`person_id IS NULL`) inside the horizon. */
  select(tripId: string, householdId: string): Promise<Availability[]>
  /** Delete household-level rows inside the horizon, then insert these. */
  replace(tripId: string, householdId: string, rows: Omit<Availability, 'id'>[]): Promise<void>
}

const TABLE = 'availability'

/**
 * Delete-and-reinsert persistence against Supabase, scoped to
 * `(trip_id, household_id, person_id IS NULL, start_date >= horizonStart, end_date <= horizonEnd)`.
 *
 * Idempotent, no diffing. Person-level rows are never touched. RLS (spz-01) is what
 * restricts writes to the caller's own household — the anon client carrying the
 * user's JWT is sufficient, no service role.
 */
export function supabaseAvailabilityClient(
  supabase: SupabaseClient,
  horizonStart: ISODate,
  horizonEnd: ISODate,
): AvailabilityClient {
  return {
    async select(tripId, householdId) {
      const { data, error } = await supabase
        .from(TABLE)
        .select('*')
        .eq('trip_id', tripId)
        .eq('household_id', householdId)
        .is('person_id', null)
        .gte('start_date', horizonStart)
        .lte('end_date', horizonEnd)
        .order('start_date', { ascending: true })
      if (error) throw new Error(error.message)
      return (data ?? []) as Availability[]
    },

    async replace(tripId, householdId, rows) {
      const del = await supabase
        .from(TABLE)
        .delete()
        .eq('trip_id', tripId)
        .eq('household_id', householdId)
        .is('person_id', null)
        .gte('start_date', horizonStart)
        .lte('end_date', horizonEnd)
      if (del.error) throw new Error(del.error.message)
      if (rows.length === 0) return
      const ins = await supabase.from(TABLE).insert(rows)
      if (ins.error) throw new Error(ins.error.message)
    },
  }
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export interface AvailabilityStoreStatus {
  /** Local edits not yet persisted (either debouncing or a failed save awaiting retry). */
  dirty: boolean
  /** A `replace()` is in flight. */
  saving: boolean
  /** Message from the last failed save, cleared on success. */
  error: string | null
}

export interface AvailabilityStore {
  load(): Promise<Map<ISODate, AvailabilityLevel>>
  /** Optimistic: mutates in memory, notifies, and schedules a debounced `replace()`. */
  setLevel(dates: ISODate[], level: AvailabilityLevel): void
  /** Cancel the debounce and save now. Call from the component's unmount cleanup. */
  flush(): Promise<void>
  subscribe(fn: (days: Map<ISODate, AvailabilityLevel>) => void): () => void
  /** Snapshot of the current day map. */
  getDays(): Map<ISODate, AvailabilityLevel>
  getStatus(): AvailabilityStoreStatus
}

export interface AvailabilityStoreOptions {
  client: AvailabilityClient
  tripId: string
  householdId: string
  horizonStart: ISODate
  horizonEnd: ISODate
  /** Default 800 ms. */
  debounceMs?: number
  onSaved?: () => void
}

export function createAvailabilityStore(opts: AvailabilityStoreOptions): AvailabilityStore {
  const { client, tripId, householdId, horizonStart, horizonEnd, debounceMs = 800, onSaved } = opts

  let days = expandRows([], horizonStart, horizonEnd)
  let status: AvailabilityStoreStatus = { dirty: false, saving: false, error: null }
  let timer: ReturnType<typeof setTimeout> | null = null
  /** Bumped on every accepted edit so a save can tell if it raced with a tap. */
  let version = 0

  const listeners = new Set<(d: Map<ISODate, AvailabilityLevel>) => void>()
  const notify = () => {
    const snapshot = new Map(days)
    for (const fn of listeners) fn(snapshot)
  }

  const cancelTimer = () => {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
  }

  async function save(): Promise<void> {
    cancelTimer()
    if (!status.dirty || status.saving) return
    status = { ...status, saving: true }
    notify()

    const savedVersion = version
    const rows: Omit<Availability, 'id'>[] = coalesce(days).map((r) => ({
      ...r,
      trip_id: tripId,
      household_id: householdId,
    }))

    try {
      await client.replace(tripId, householdId, rows)
      // A tap that landed mid-flight leaves the store dirty; the next timer or
      // flush() picks it up rather than this save falsely marking it clean.
      const raced = version !== savedVersion
      status = { dirty: raced, saving: false, error: null }
      notify()
      onSaved?.()
      if (raced) schedule()
    } catch (err) {
      // Keep the dirty flag so the next timer or flush() retries.
      status = { dirty: true, saving: false, error: err instanceof Error ? err.message : String(err) }
      notify()
    }
  }

  const schedule = () => {
    cancelTimer()
    timer = setTimeout(() => {
      timer = null
      void save()
    }, debounceMs)
  }

  return {
    async load() {
      const rows = await client.select(tripId, householdId)
      days = expandRows(rows, horizonStart, horizonEnd)
      notify()
      return new Map(days)
    },

    setLevel(dates, level) {
      let changed = false
      for (const d of dates) {
        // Days outside the horizon are not in the map and are never persisted.
        if (!days.has(d)) continue
        if (days.get(d) === level) continue
        days.set(d, level)
        changed = true
      }
      if (!changed) return
      version += 1
      status = { ...status, dirty: true }
      notify()
      schedule()
    },

    async flush() {
      cancelTimer()
      await save()
    },

    subscribe(fn) {
      listeners.add(fn)
      return () => {
        listeners.delete(fn)
      }
    },

    getDays() {
      return new Map(days)
    },

    getStatus() {
      return status
    },
  }
}
